import React, {useEffect, useMemo} from 'react';
import {
    Avatar,
    Backdrop,
    Box,
    Button,
    ClickAwayListener,
    InputAdornment,
    ListItemButton,
    ListItemSecondaryAction,
    ListSubheader,
    Slide,
    TextField
} from '@mui/material';
import {lighten, Theme} from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import List from "@mui/material/List";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import clsx from "clsx";
import DoneIcon from "@mui/icons-material/Done";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import EditSportObject from "../../Dialogs/EditSportObject";
import AddSportObject from "../../Dialogs/AddSportObject";
import {useTheme} from "@mui/styles";
import {useStores} from '../../../../../../store';
import {Observer} from 'mobx-react-lite';
import {SportObjectEntity} from "../../../../../../graphql/graphQlApiHooks";
import InfoDialog from "components/Dialogs/InfoDialog";

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        "& $editObject, $addObject": {
            padding: '0 14px',
            '& .edit-avatar, .add-avatar': {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '1em',
                '& .MuiAvatar-root': {
                    background: '#747bff'
                }
            },
            '& .input-wrapper': {
                '& .MuiFormControl-root': {
                    marginBottom: '.6em',
                },
                "& .MuiFilledInput-root": {
                    borderRadius: theme.shape.borderRadius,
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    transition: 'background-color 0.2s ease, border-color 0.2s ease',
                    '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.15)',
                    },
                    '&.Mui-focused': {
                        backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    },
                    '& .MuiFilledInput-input': {
                        color: 'white',
                        padding: '16px 12px',
                        fontSize: '14px',
                        '&::placeholder': {
                            color: 'rgba(255, 255, 255, 0.7)',
                            opacity: 1,
                        }
                    }
                },
                "& .MuiInputLabel-root": {
                    color: 'rgba(255, 255, 255, 0.8)',
                    fontSize: '14px',
                    '&.Mui-focused': {
                        color: 'white',
                    }
                }
            },

        }
    },
    sportObjectsListContent: {
        height: 'calc(100% - 60px)',
        overflow: 'auto',
        position: 'absolute',
        width: '100%',
        left: 0,
        padding: '8px 16px',
        '& .MuiListItemButton-root': {
            borderRadius: theme.shape.borderRadius,
            padding: theme.spacing(0.75, 1),
            marginBottom: theme.spacing(0.25),
            transition: 'background-color 0.2s ease',
            '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
            },
            '&.Mui-selected': {
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.25)',
                }
            },
            '& .MuiListItemIcon-root': {
                marginRight: theme.spacing(1),
                color: 'inherit',
                minWidth: 'auto',
                '& .MuiSvgIcon-root': {
                    fontSize: '18px',
                }
            },
            '& .MuiListItemText-primary': {
                fontWeight: 400,
                fontSize: '14px',
                color: 'white',
            }
        }
    },
    backdrop: {
        zIndex: 1200,
    },
    searchInput: {
        "& .MuiOutlinedInput-root": {
            background: 'rgba(255, 255, 255, 0.9)',
            borderRadius: theme.shape.borderRadius,
            transition: 'background-color 0.2s ease, border-color 0.2s ease',
            '&:hover': {
                background: 'rgba(255, 255, 255, 0.95)',
                '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#999',
                }
            },
            '&.Mui-focused': {
                background: 'white',
                '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.primary.main,
                    borderWidth: 2,
                }
            },
            '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#ccc',
            }
        },
        "& .MuiInputBase-input": {
            padding: '12px 14px',
            fontSize: '14px',
            color: theme.palette.text.primary,
            '&::placeholder': {
                color: '#666',
                opacity: 1,
                fontSize: '14px',
            }
        },
        "& .MuiInputAdornment-root": {
            '& .MuiSvgIcon-root': {
                color: '#666',
                transition: 'color 0.2s ease',
            }
        },
        "&:hover .MuiInputAdornment-root .MuiSvgIcon-root": {
            color: '#333',
        },
        "&.Mui-focused .MuiInputAdornment-root .MuiSvgIcon-root": {
            color: theme.palette.primary.main,
        }
    },

    list: {
        maxWidth: 360,
        borderRadius: theme.shape.borderRadius,
        '& .MuiListSubheader-root': {
            letterSpacing: '0.05em',
            color: 'white',
            fontSize: 14,
            lineHeight: '14px',
            fontWeight: 500,
            padding: '0 0 16px 0',
        },
        '& .MuiListItem-root': {
            padding: theme.spacing(0),
            '& .MuiList-root': {
                padding: 0,
                width: '100%',
                '& .MuiButtonBase-root': {
                    marginTop: theme.spacing(0.5)
                }
            },
            '& .MuiListItemText-primary': {
                fontWeight: 400,
                fontSize: '14px',
                color: 'white',
            },
            '&.Mui-selected': {
                color: 'white',
                backgroundColor: 'rgba(255, 255, 255, 0.15)',
                '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                },
                '& $icon': {
                    color: 'inherit'
                }
            },
        }
    },
    icon: {
        color: 'white',
        minWidth: 40
    },

    sportObjectsList: {
        flexShrink: 0,
        borderRadius: 8,
        padding: '0',
        position: 'relative',
        height: 100,
        '& .MuiListSubheader-root': {
            padding: '16px 14px !important',
            color: 'inherit',
            userSelect: 'none',
            backgroundColor: 'transparent',
            '& .MuiListItemSecondaryAction-root': {
                right: 8
            }
        },
        '& .MuiListItem-root': {
            userSelect: 'none',
            '& .centered': {
                textAlign: 'center'
            },
            '& .MuiListItemSecondaryAction-root': {
                right: 10
            }
        },
        '& $icon': {
            marginLeft: '14px',
        },
        '& $list': {
            transition: 'height 0.3s ease',
            backgroundColor: '#4851f9',
            color: theme.palette.background.paper,
            height: 100,
            overflow: 'hidden',
            padding: 0,
            zIndex: 1,
            '& .MuiAutocomplete-popper': {
                width: '100% !important',
                height: 'calc(100% - 56px)',
                '& .MuiAutocomplete-listbox': {
                    height: '100%',
                }
            },
            '&.isOpen': {
                boxShadow: '-1px 2px 16px rgb(0 0 0 / 20%)',
                zIndex: 100000,
                height: '400px',
                '& $objectsBottomPanel': {height: 'auto', opacity: 1},
                '& $objectsList': {
                    height: 'calc(100% - 40px)'
                },
            },
        }
    },

    objectsListContent: {
        height: 'calc(100% - 50px)', // размер заголовка
    },
    objectsBottomPanel: {
        opacity: 0,
        overflow: 'hidden',
        height: 0,
        padding: '8px 12px',
        display: 'flex',
        justifyContent: 'space-between',
        flexWrap: 'nowrap',
        transition: 'opacity 0.3s ease',
        '& .MuiButton-root': {
            fontSize: 12,
            background: 'white',
            padding: '6px 12px',
            borderRadius: theme.shape.borderRadius,
            '&:hover': {
                backgroundColor: '#f5f5f5',
            }
        }
    },
    listWrapper: {
        position: 'relative',
        display: 'flex',
        height: 'calc(100% - 40px)', // Размер подвала
        '& $listItem': {
            minWidth: '100%',
            maxWidth: '100%',
            position: 'absolute',
            height: '100%',
        },
        '& .MuiListItem-root .MuiListItemText-root .MuiTypography-root':{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            width: '85%',
            color: 'white'
        }

    },
    listItem: {
        '& .MuiTypography-root': {
            color: 'white'
        }
    },

    objectsList: {
        overflow: 'hidden',
        width: '100%',
        zIndex: 100000,
        height: '0',
    },
    deleteConfirm: {
        display: 'flex',
        alignItems: 'center',
        padding: 16,
        '& .MuiBox-root': {
            padding: '0 16px',
            background: 'white',
            borderRadius: theme.shape.borderRadius,
            border: '1px solid rgba(255, 37, 47, 0.2)',
        },
        '& h4': {
            fontSize: 14,
            borderBottom: '1px solid rgba(255, 37, 47, 0.2)',
            padding: '12px 0',
            color: theme.palette.secondary.main
        },
        '& p': {
            padding: '12px 0',
            fontSize: 14,
            color: theme.palette.text.primary
        },
    },
    objectsSearchPanel: {
        height: '100%',
    },
    editObject: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        padding: '0 16px',
        overflow: 'auto',
        maxHeight: '100%',
    },
    addObject: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        padding: '0 16px',
        overflow: 'auto',
        maxHeight: '100%',
    },

    popper: {
        border: '1px solid rgba(27,31,35,.15)',
        boxShadow: '0 3px 12px rgba(27,31,35,.15)',
        borderRadius: 3,
        width: 300,
        zIndex: 1,
        fontSize: 13,
        color: '#586069',
        backgroundColor: '#f6f8fa',
    },
    header: {
        borderBottom: '1px solid #e1e4e8',
        padding: '8px 10px',
        fontWeight: 600,
    },
    inputBase: {
        padding: 10,
        width: '100%',
        // borderBottom: '1px solid #dfe2e5',
        '& input': {
            borderRadius: 4,
            backgroundColor: theme.palette.common.white,
            padding: 8,
            transition: theme.transitions.create(['border-color', 'box-shadow']),
            border: '1px solid #ced4da',
            fontSize: 14,
            '&:focus': {
                boxShadow: `rgba(${theme.palette.primary.main}, .25) 0 0 0 0.2rem`,
                borderColor: theme.palette.primary.main,
            },
        },
    },
    paper: {
        boxShadow: 'none',
        margin: 0,
        color: '#586069',
        fontSize: 13,
        background: 'transparent',
        height: '100%',

    },
    option: {
        minHeight: 'auto',
        alignItems: 'flex-start',
        padding: 8,
        '&[aria-selected="true"]': {
            backgroundColor: lighten(theme.palette.primary.main, .85),
            '& .MuiSvgIcon-root': {
                color: theme.palette.primary.main,
            }
        },
        '&[data-focus="true"]': {
            backgroundColor: theme.palette.action.hover,
        },
    },
    popperDisablePortal: {
        position: 'relative',
    },
    iconSelected: {
        width: 17,
        height: 17,
        marginRight: 5,
        marginLeft: -2,
    },
    text: {
        flexGrow: 1,
    },
    close: {
        opacity: 0.6,
        width: 18,
        height: 18,
    },
}));


function SportObjectsList() {
    const theme = useTheme();
    const classes = useStyles();
    const {settingsStore} = useStores();

    const [isOpenObjectsList, setIsOpenObjectsList] = React.useState(false);
    const [objectsSearchPanelVisible, setObjectsSearchPanelVisible] = React.useState(true);
    const [objectsDeleteConfirmVisible, setObjectsDeleteConfirmVisible] = React.useState(false);
    const [objectsEditConfirmVisible, setObjectsEditConfirmVisible] = React.useState(false);
    const [objectsEditConfirmDirection, setObjectsEditConfirmDirection] = React.useState<"left" | "right" | "up" | "down" | undefined>("left");
    const [objectsAddConfirmVisible, setObjectsAddConfirmVisible] = React.useState(false);
    const [sportObjects, setSportObjects] = React.useState<SportObjectEntity[]>([])
    const [searchValue, setSearchValue] = React.useState<string>('')
    const [editObjectFormOpen, setEditObjectFormOpen] = React.useState(false);
    const [deleteErrorDialog, setDeleteErrorDialog] = React.useState(false);


    useEffect(() => {
        setSportObjects(([...settingsStore.sportObjects]
            .filter(obj => {
                return obj.name.toLowerCase().includes(searchValue.toLowerCase())
            })
            .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase(), 'ru'))
        ))
    }, [settingsStore.sportObjects, searchValue])

    const handleListItemClick = (
        event: React.MouseEvent<HTMLDivElement, MouseEvent>,
        sportObjectId: string,
    ) => {
        settingsStore.setSportObject(sportObjectId)
    };


    const deleteCurrentObject = () => {
        if (settingsStore.sportObject) {
            if (!settingsStore.hasEquipments()) {
                settingsStore.sportObjectDelete(settingsStore.sportObject.id);
                setSearchValue('')
            } else {
                setDeleteErrorDialog(true)
            }
            showSearchPanelSlide()
        }
    };

    const openListHandler = () => {
        setIsOpenObjectsList(!isOpenObjectsList)
        setObjectsSearchPanelVisible(true)
        setObjectsDeleteConfirmVisible(false)
        setObjectsEditConfirmVisible(false)
        setObjectsAddConfirmVisible(false)
    };
    const closeListHandler = () => {
        setIsOpenObjectsList(false)
        setObjectsSearchPanelVisible(true)
        setObjectsDeleteConfirmVisible(false)
        setObjectsEditConfirmVisible(false)
        setObjectsAddConfirmVisible(false)
    };

    const showSearchPanelSlide = () => {
        setEditObjectFormOpen(false)
        setObjectsAddConfirmVisible(false)
        setObjectsEditConfirmVisible(false)
        setObjectsDeleteConfirmVisible(false)
        setObjectsSearchPanelVisible(true)
    };
    const showDeleteConfirmSlide = () => {
        setObjectsEditConfirmDirection('right')
        setObjectsDeleteConfirmVisible(objectsEditConfirmVisible)
        setObjectsEditConfirmVisible(!objectsEditConfirmVisible)
    };
    const showEditSportObjectSlide = () => {
        setEditObjectFormOpen(!editObjectFormOpen)
        setObjectsEditConfirmVisible(objectsSearchPanelVisible)
        setObjectsEditConfirmDirection('left')
        setObjectsSearchPanelVisible(!objectsSearchPanelVisible)
    };
    const showCreateSportObjectSlide = () => {
        setObjectsAddConfirmVisible(!objectsAddConfirmVisible)
        setObjectsSearchPanelVisible(!objectsSearchPanelVisible)
    };

    const BottomPanel = () => {
        if (objectsDeleteConfirmVisible) {
            return (
                <>
                    <Box>
                        <Button
                            size={'small'}
                            color='primary'
                            onClick={showDeleteConfirmSlide}
                        >Отмена</Button>
                    </Box>
                    <Button
                        size={'small'}
                        color='secondary'
                        onClick={deleteCurrentObject}
                    >Удалить</Button>
                </>
            );
        }
        if (objectsEditConfirmVisible) {
            return (
                <>
                    <Box>
                        <Button
                            size={'small'}
                            color={"error"}
                            onClick={showEditSportObjectSlide}
                        >Отмена</Button>
                    </Box>
                    <Button
                        size={'small'}
                        color='primary'
                        type='submit'
                        form="edit_object"
                    >Сохранить</Button>
                </>
            );
        }
        if (objectsAddConfirmVisible) {
            return (
                <>
                    <Box>
                        <Button
                            size={'small'}
                            onClick={showCreateSportObjectSlide}
                        >Отмена</Button>
                    </Box>
                    <Button
                        size={'small'}
                        color='primary'
                        type='submit'
                        form='add_object'
                    >Добавить</Button>
                </>
            );
        }

        return (
            <>
                <Box>
                    <Button
                        size={'small'}
                        onClick={showEditSportObjectSlide}
                    >Изменить</Button>
                </Box>
                {!settingsStore.isRefetching && (
                    <Button
                        size={'small'}
                        onClick={showCreateSportObjectSlide}
                    >Добавить</Button>
                )}
            </>
        );
    };


    useMemo(() => {
        if (settingsStore.sportObjects[0]) {
            showSearchPanelSlide()
        } else {
            // Показываем форму создания только если данные загружены, объектов нет и не идет обновление
            if (isOpenObjectsList && !settingsStore.isRefetching) {
                showCreateSportObjectSlide()
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [settingsStore.sportObjects, isOpenObjectsList, settingsStore.isRefetching])

    return <Observer>{() => (
        <React.Fragment>
            <Box className={clsx(classes.sportObjectsList, classes.root)}>

                <ClickAwayListener onClickAway={closeListHandler}>
                    <List
                        component="nav"
                        aria-labelledby="nested-list-subheader"
                        className={isOpenObjectsList ? 'isOpen ' + classes.list : classes.list}
                        onClick={!isOpenObjectsList ? openListHandler : undefined}
                        style={{ cursor: !isOpenObjectsList ? 'pointer' : 'default' }}

                        subheader={
                            <ListSubheader
                                onClick={(e) => {
                                    e.stopPropagation();
                                    openListHandler();
                                }}
                                component="div"
                                id="nested-list-subheader"
                            >
                                Спортивный объекты

                                <ListItemSecondaryAction onClick={(e) => {
                                    e.stopPropagation();
                                    openListHandler();
                                }}>
                                    <UnfoldMoreIcon fontSize={'small'}/>
                                </ListItemSecondaryAction>
                            </ListSubheader>
                        }
                    >
                        {settingsStore.sportObjects &&

                        <div className={classes.objectsListContent}>
                            <div className={classes.listWrapper}>
                                <Slide
                                    direction={'right'}
                                    in={objectsSearchPanelVisible}
                                    appear={false}
                                >
                                    <div className={classes.listItem}>
                                        {settingsStore.sportObject ? (
                                            <ListItem onClick={(e) => {
                                                e.stopPropagation();
                                                openListHandler();
                                            }}>
                                                <ListItemIcon classes={{root: classes.icon}}>
                                                    <Avatar
                                                        sx={{
                                                            background: '#747bff',
                                                            width: 32,
                                                            height: 32
                                                        }}
                                                    />
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={settingsStore.sportObjects.find((obj: SportObjectEntity) => {
                                                        return obj.id === settingsStore.sportObject.id
                                                    }).name}
                                                />
                                            </ListItem>) : !settingsStore.isRefetching ? (
                                            <Button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    openListHandler()
                                                    showCreateSportObjectSlide()
                                                }}
                                                color={'info'}
                                                sx={{
                                                    display: 'flex',
                                                    m: 'auto',
                                                    bgcolor: theme.palette.background.default,
                                                    "&:hover": {
                                                        bgcolor: theme.palette.background.paper,
                                                    }
                                                }}
                                            >
                                                Добавьте спортивный объект
                                            </Button>
                                        ) : null
                                        }
                                        <List
                                            disablePadding
                                            className={
                                                clsx(
                                                    classes.objectsList,
                                                    objectsSearchPanelVisible ? 'visible' : null
                                                )
                                            }
                                        >
                                            <div
                                                className={
                                                    clsx(
                                                        classes.objectsSearchPanel,
                                                        objectsSearchPanelVisible ? 'visible' : null
                                                    )}
                                            >

                                                <Box
                                                    sx={{p: 2}}
                                                >
                                                    <TextField
                                                        id="search-sport-object"
                                                        variant="outlined"
                                                        fullWidth={true}
                                                        size={'small'}
                                                        value={searchValue}
                                                        className={classes.searchInput}
                                                        placeholder="Поиск спортивных объектов..."
                                                        InputProps={{
                                                            startAdornment: (
                                                                <InputAdornment position="start">
                                                                    <SearchIcon 
                                                                        sx={{ 
                                                                            color: 'text.secondary',
                                                                            fontSize: '20px'
                                                                        }} 
                                                                    />
                                                                </InputAdornment>
                                                            ),
                                                            endAdornment: searchValue && (
                                                                <InputAdornment position="end">
                                                                    <Button
                                                                        size="small"
                                                                        onClick={() => {
                                                                            setSearchValue('')
                                                                        }}
                                                                        sx={{ 
                                                                            minWidth: 'auto',
                                                                            padding: '4px',
                                                                            color: 'text.secondary',
                                                                            '&:hover': {
                                                                                backgroundColor: 'action.hover'
                                                                            }
                                                                        }}
                                                                    >
                                                                        <ClearIcon fontSize="small" />
                                                                    </Button>
                                                                </InputAdornment>
                                                            )
                                                        }}
                                                        onChange={(event: { target: { value: string } }) => {
                                                            setSearchValue(event.target.value)
                                                        }}
                                                    />

                                                    {/* Показываем количество найденных результатов */}
                                                    {searchValue && (
                                                        <Box sx={{ 
                                                            px: 2, 
                                                            py: 1, 
                                                            fontSize: '12px', 
                                                            color: 'rgba(255, 255, 255, 0.8)',
                                                            borderBottom: '1px solid',
                                                            borderColor: 'rgba(255, 255, 255, 0.2)'
                                                        }}>
                                                            Найдено: {sportObjects.length} из {settingsStore.sportObjects.length}
                                                        </Box>
                                                    )}

                                                    <List
                                                        component="nav"
                                                        className={classes.sportObjectsListContent}
                                                    >
                                                        {sportObjects.length > 0 ? (
                                                            sportObjects.map((sportObject: SportObjectEntity, index: number) => {
                                                                return (
                                                                    <ListItemButton
                                                                        key={index}
                                                                        selected={sportObject.id === settingsStore?.sportObject?.id}
                                                                        onClick={(event) => handleListItemClick(event, sportObject.id)}
                                                                    >
                                                                        <ListItemIcon>
                                                                            <DoneIcon style={{visibility: (sportObject.id === settingsStore?.sportObject?.id) ? 'visible' : 'hidden'}}/>
                                                                        </ListItemIcon>
                                                                        <ListItemText primary={sportObject.name}/>
                                                                    </ListItemButton>
                                                                )
                                                            })
                                                        ) : searchValue ? (
                                                            <Box sx={{ 
                                                                p: 3, 
                                                                textAlign: 'center',
                                                                color: 'text.secondary'
                                                            }}>
                                                                <Box component="div" sx={{ fontSize: '14px', mb: 1 }}>
                                                                    По запросу "{searchValue}" ничего не найдено
                                                                </Box>
                                                                <Box component="div" sx={{ fontSize: '12px', opacity: 0.7 }}>
                                                                    Попробуйте изменить поисковый запрос
                                                                </Box>
                                                            </Box>
                                                        ) : settingsStore.sportObjects.length === 0 && !settingsStore.isRefetching ? (
                                                            <Box sx={{ 
                                                                p: 3, 
                                                                textAlign: 'center',
                                                                color: 'text.secondary'
                                                            }}>
                                                                <Box component="div" sx={{ fontSize: '14px', mb: 1 }}>
                                                                    Спортивные объекты не найдены
                                                                </Box>
                                                                <Box component="div" sx={{ fontSize: '12px', opacity: 0.7 }}>
                                                                    Добавьте первый спортивный объект
                                                                </Box>
                                                            </Box>
                                                        ) : null}


                                                    </List>


                                                </Box>
                                            </div>

                                        </List>
                                    </div>
                                </Slide>
                                {settingsStore.sportObject &&
                                <>
                                    <Slide
                                        direction={'left'}
                                        in={objectsDeleteConfirmVisible}
                                    >
                                        <div
                                            className={
                                                clsx(
                                                    classes.deleteConfirm,
                                                    objectsDeleteConfirmVisible ? 'visible' : null,
                                                    classes.listItem
                                                )}
                                        >
                                            <Box>
                                                <h4>Вы уверены, что хотите удалить {settingsStore.sportObject.name}?</h4>
                                                <p>Это действие необратимо. Чтобы подтвердить удаление, нажмите кнопку
                                                   ниже.</p>
                                            </Box>

                                        </div>
                                    </Slide>
                                    <Slide
                                        direction={objectsEditConfirmDirection}
                                        in={objectsEditConfirmVisible}
                                    >
                                        <div
                                            className={
                                                clsx(
                                                    classes.editObject,
                                                    objectsEditConfirmVisible ? 'visible' : null,
                                                    classes.listItem
                                                )}
                                        >
                                            <EditSportObject
                                                open={editObjectFormOpen}
                                                deleteOnClicked={showDeleteConfirmSlide}
                                                onCompleted={showSearchPanelSlide}
                                            />
                                        </div>
                                    </Slide>

                                </>
                                }
                                <Slide
                                    direction={'left'}
                                    in={objectsAddConfirmVisible}
                                >
                                    <div
                                        className={
                                            clsx(
                                                classes.addObject,
                                                objectsAddConfirmVisible ? 'visible' : null,
                                                classes.listItem
                                            )}
                                    >
                                        <AddSportObject
                                            open={objectsAddConfirmVisible}
                                            onAdded={() => {
                                                showSearchPanelSlide();
                                            }}
                                        />
                                    </div>
                                </Slide>
                            </div>
                            <div className={classes.objectsBottomPanel}>
                                <BottomPanel/>
                            </div>
                        </div>
                        }
                    </List>
                </ClickAwayListener>

                <Backdrop
                    open={isOpenObjectsList}
                    className={classes.backdrop}
                />
            </Box>

            <InfoDialog
                open={deleteErrorDialog}
                title={'Невозможно удалить спортивный объект'}
                message={"Этот спортивный объект содержит оборудование. Сначала удалите или переместите оборудование."}
                close={setDeleteErrorDialog}
            />

        </React.Fragment>
    )}</Observer>
}

SportObjectsList.protoTypes = {}

export default SportObjectsList;
